'use client';

import { useState } from 'react';
import { useAuthContext } from './providers/AuthProvider';
import Link from 'next/link';
import Image from 'next/image';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ds';

export default function Navbar() {
  const { user, loading } = useAuthContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  // Logo target — public marketing pages link back to /, but if a logged-in
  // user is briefly on this navbar (sign-in transition window), point them
  // at /home so the logo click resolves to their authed surface.
  const logoHref = !loading && user ? '/home' : '/';

  return (
    <nav className="fixed w-full top-0 z-50 bg-surface-1/95 backdrop-blur-md border-b border-border-subtle">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <div className="flex-shrink-0">
            {loading ? (
              <span className="flex items-center gap-2 cursor-not-allowed opacity-60">
                <Image
                  src="/indiestrylogofull.png"
                  alt="Indiestry"
                  width={32}
                  height={32}
                  priority
                />
                <span className="text-base font-semibold text-text-primary">
                  Indiestry
                </span>
              </span>
            ) : (
              <Link
                href={logoHref}
                className="flex items-center gap-2 hover:opacity-90 transition-opacity"
              >
                <Image
                  src="/indiestrylogofull.png"
                  alt="Indiestry"
                  width={32}
                  height={32}
                  priority
                />
                <span className="text-base font-semibold text-text-primary">
                  Indiestry
                </span>
              </Link>
            )}
          </div>

          <div className="hidden md:flex items-center gap-2">
            {/* Public marketing navbar — only ever shows the logged-out
                action cluster. If user is set we're in the brief sign-in
                transition window and the route is about to swap to the
                authed chrome (NavbarAuth); render nothing rather than
                flash a stale Explore/Profile/Log Out set of links. */}
            {!loading && !user && (
              <>
                <Link href="/login">
                  <Button variant="ghost" size="sm">
                    Log In
                  </Button>
                </Link>
                <Link href="/signup">
                  <Button variant="primary" size="sm">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>

          <div className="md:hidden flex items-center">
            <button
              type="button"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              className="w-9 h-9 flex items-center justify-center rounded-sm text-text-secondary hover:text-text-primary"
            >
              {isMenuOpen ? (
                <X className="w-5 h-5" strokeWidth={1.75} />
              ) : (
                <Menu className="w-5 h-5" strokeWidth={1.75} />
              )}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden pb-3">
            <div className="px-2 py-2 space-y-1 bg-surface-2 border border-border-subtle rounded-md mt-2">
              {!loading && !user ? (
                <>
                  <Link
                    href="/login"
                    onClick={closeMenu}
                    className="block px-3 py-2 rounded-sm text-sm text-text-secondary hover:text-text-primary hover:bg-surface-3 transition-colors"
                  >
                    Log In
                  </Link>
                  <Link
                    href="/signup"
                    onClick={closeMenu}
                    className="block px-3 py-2 rounded-sm text-sm font-medium text-text-on-accent bg-accent hover:bg-accent-hover transition-colors"
                  >
                    Sign Up
                  </Link>
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
